import { useState } from 'react';
import { ExternalLink, MapPin, Package, Trash2, X } from 'lucide-react';

import { useDismissLead, useLeadDetail } from '../hooks';

function pesos(amount: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount);
}

interface Props {
  leadId: number | null;
  onClose: () => void;
  onDismissed: () => void;
}

export function LeadDrawer({ leadId, onClose, onDismissed }: Props) {
  const { detail, loading, error } = useLeadDetail(leadId);
  const { dismiss, loading: dismissing } = useDismissLead();
  const [dismissReason, setDismissReason] = useState('');

  if (leadId === null) return null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <aside
        className="absolute right-0 top-0 h-full w-full max-w-lg overflow-y-auto border-l border-white/10 bg-[#0e1115]/95 shadow-2xl backdrop-blur-xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          background:
            'linear-gradient(180deg, rgba(14,17,21,.96) 0%, rgba(11,15,20,.92) 100%)',
        }}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0e1115]/90 px-4 py-3 backdrop-blur">
          <h2 className="text-sm font-semibold text-white/80">Detalle del lead</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"
          >
            <X size={16} />
          </button>
        </header>

        {loading ? (
          <div className="p-6 text-sm text-white/50">Cargando...</div>
        ) : error ? (
          <div className="m-4 rounded border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
        ) : !detail ? (
          <div className="p-6 text-sm text-white/50">No encontrado.</div>
        ) : (
          <div className="space-y-4 p-4">
            <section>
              <div className="flex items-start justify-between gap-3">
                <h3 className="flex-1 text-base font-semibold text-white">{detail.project.title}</h3>
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-base font-bold text-emerald-300 ring-2 ring-emerald-500/40">
                  {detail.score}
                </span>
              </div>
              {detail.project.fuente_url ? (
                <a
                  href={detail.project.fuente_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-xs text-sky-400 hover:underline"
                >
                  Ver en Compranet <ExternalLink size={12} />
                </a>
              ) : null}
            </section>

            <section className="o-card grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-white/40">Procedimiento</div>
                <div className="font-mono text-[11px] text-white/80">{detail.project.external_id}</div>
              </div>
              <div>
                <div className="text-white/40">Tipo</div>
                <div className="text-white/80">{detail.project.project_type}</div>
              </div>
              <div>
                <div className="text-white/40">Unidad compradora</div>
                <div className="text-white/80">{detail.project.contractor_name || '—'}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-white/40"><MapPin size={11} /> Estado</div>
                <div className="text-white/80">{detail.project.state || '—'}</div>
              </div>
              <div>
                <div className="text-white/40">Fecha publicación</div>
                <div className="text-white/80">{detail.project.start_date || '—'}</div>
              </div>
              <div>
                <div className="text-white/40">Monto</div>
                <div className="font-semibold text-white/90">{pesos(detail.project.amount_mxn)}</div>
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-white/50">Breakdown del score</h4>
              <div className="space-y-2">
                <ScoreBar label="Match inventario" value={detail.score_breakdown.inventory_match ?? 0} weight={detail.score_breakdown.weights?.inventory ?? 0.5} />
                <ScoreBar label="Cobertura geográfica" value={detail.score_breakdown.geo_fit ?? 0} weight={detail.score_breakdown.weights?.geo ?? 0.3} />
                <ScoreBar label="Urgencia (plazo)" value={detail.score_breakdown.urgency ?? 0} weight={detail.score_breakdown.weights?.urgency ?? 0.2} />
              </div>
            </section>

            <section>
              <h4 className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-white/50">
                <Package size={12} />
                Inventario que matchea ({detail.matched_inventory.length})
              </h4>
              {detail.matched_inventory.length === 0 ? (
                <div className="rounded border border-white/10 bg-white/[0.02] p-3 text-xs text-white/50">
                  Sin items de inventario que matcheen — el score viene principalmente de geo + urgencia.
                </div>
              ) : (
                <ul className="space-y-1">
                  {detail.matched_inventory.map((it) => (
                    <li key={it.id} className="flex items-center justify-between rounded border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/80">
                      <span>{it.name}</span>
                      <span className="text-white/40">{it.category}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {detail.outreach_log.length > 0 ? (
              <section>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-white/50">Historial de outreach</h4>
                <ul className="space-y-1 text-xs">
                  {detail.outreach_log.map((entry, i) => (
                    <li key={i} className="rounded border border-white/10 bg-white/[0.02] p-2 text-white/70">
                      <div className="font-medium text-white/90">{entry.action || entry.channel}</div>
                      <div className="text-[10px] text-white/40">{entry.ts}</div>
                      {entry.reason ? <div className="mt-1 italic text-white/60">{entry.reason}</div> : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="rounded-xl border border-red-500/30 bg-red-500/[0.04] p-3">
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-red-300">Descartar lead</h4>
              <input
                type="text"
                placeholder="Razón (opcional)"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                className="input-base mb-2"
              />
              <button
                type="button"
                disabled={dismissing || detail.status === 'dismissed'}
                onClick={async () => {
                  await dismiss(detail.id, dismissReason);
                  onDismissed();
                }}
                className="inline-flex items-center gap-1 rounded-md bg-red-500/80 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-40"
              >
                <Trash2 size={12} />
                {detail.status === 'dismissed' ? 'Ya descartado' : 'Descartar'}
              </button>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function ScoreBar({ label, value, weight }: { label: string; value: number; weight: number }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div>
      <div className="mb-1 flex justify-between text-[11px] text-white/60">
        <span>
          {label} <span className="text-white/30">· peso {Math.round(weight * 100)}%</span>
        </span>
        <span className="font-mono text-white/80">{value}/100</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
